/* Public auth endpoints. All are unauthenticated (`auth: false`) — attaching a
   stale bearer to a login request would be harmless but misleading in the
   network log, and the reset flow must never trigger a refresh. */
import { request } from '../lib/apiClient'

const pub = { auth: false }

// Login/signup set the HttpOnly refresh cookie; `include` makes the browser keep
// it even when VITE_API_BASE points at another origin.
const withCookie = { ...pub, credentials: 'include' }

export function login(email, password) {
  return request('/users/login', { ...withCookie, method: 'POST', body: { email, password } })
}

/** `roleId` is a REQUEST: the account gets no role until an admin approves it in
 *  Settings → Approvals (and may change it). Departments are picked after the
 *  email is confirmed (DepartmentSetup), never on this public form. */
export function signup(name, email, password, { roleId = null } = {}) {
  return request('/users/signup', {
    ...withCookie,
    method: 'POST',
    body: { name, email, password, role_id: roleId },
  })
}

/** Roles a signup may request — `{data:{roles:[{id,name,departments:'head'|'manager'|null}]}}`. */
export function signupOptions() {
  return request('/users/signup/options', pub)
}

/** Sign-in while approval is pending: `{data:{setup_token}}` — a token when this
 *  Department Manager signup still has to pick departments, else null. */
export function signupSetupToken(email, password) {
  return request('/users/signup/setup-token', { ...pub, method: 'POST', body: { email, password } })
}

/** Departments with their heads, for a verified signup holding a setup_token. */
export function signupDepartmentOptions(setupToken) {
  return request('/users/signup/departments/options', { ...pub, method: 'POST', body: { setup_token: setupToken } })
}

export function saveSignupDepartments(setupToken, departmentIds) {
  return request('/users/signup/departments', {
    ...pub,
    method: 'PUT',
    body: { setup_token: setupToken, department_ids: departmentIds },
  })
}

export function confirmEmail(token) {
  return request('/users/confirm-email', { ...pub, method: 'POST', body: { token } })
}

export function resendConfirmEmail(email) {
  return request('/users/confirm-email/resend', { ...pub, method: 'POST', body: { email } })
}

export function forgetPassword(email) {
  return request('/users/forget-password', { ...pub, method: 'POST', body: { email } })
}

/** `{expiresIn, resendAfter}` seconds from a forgetPassword response (null when absent). */
export function pickResetTiming(payload) {
  const src = payload?.data && typeof payload.data === 'object' ? payload.data : payload || {}
  const expiresIn = src.expires_in
  const resendAfter = src.resend_after
  return {
    expiresIn: expiresIn != null && Number.isFinite(Number(expiresIn)) ? Number(expiresIn) : null,
    resendAfter:
      resendAfter != null && Number.isFinite(Number(resendAfter)) ? Number(resendAfter) : null,
  }
}

/** The reset JWT from a verifyForgetCode response, wherever the envelope put it. */
export function pickResetToken(payload) {
  if (!payload) return null
  if (payload.reset_token) return payload.reset_token
  if (payload.access_token) return payload.access_token
  if (payload.data?.reset_token) return payload.data.reset_token
  if (payload.data?.access_token) return payload.data.access_token
  return null
}

export function verifyForgetCode(email, code) {
  return request('/users/forget-password/verify-code', {
    ...pub,
    method: 'POST',
    body: { email, code },
  })
}

export function setNewPassword(password, resetToken) {
  // `token` is the 10-minute reset JWT, carried in its own Authorization header
  // and checked by forget_password/permissions.py's HTTPBearer. Passing `token`
  // explicitly also suppresses the refresh path, which is what we want: a reset
  // token is not a session.
  return request('/users/forget-password/new-password', {
    method: 'POST',
    body: { password },
    token: resetToken,
  })
}
